/**
 * A-side (phone) model for “stop monitoring a session” (v0.5.0, §5/§9).
 *
 * Two separate concerns live here, both without React/WebSocket:
 *
 * 1. Signed `session.command` payloads with the additive `ignore` / `unignore`
 *    actions. They reuse the existing canonical Ed25519 transcript from
 *    `@luminis/protocol` and MUST NOT carry `text` or `mode`. The protocol
 *    action union (v0.5.0) now includes both actions; this module keeps a
 *    narrow payload type so a control action can never be built/signed here.
 *
 * 2. The local persisted exclusion list (`localStorage`). Until the relay gains
 *    a dedicated “list exclusions” message, this list is the UI source for the
 *    settings sections. It is reconciled from `session.command.result` events
 *    (optimistic hide, rollback on a definite failure, uncertain states kept so
 *    the user can always restore). Entries are keyed `source|session_id`.
 *
 * No command text, token, key or path is ever logged or stored here; error copy
 * goes through the same redaction as the control panel.
 */
import {
  SESSION_COMMAND_ID_PATTERN,
  SESSION_COMMAND_NONCE_PATTERN,
  SESSION_COMMAND_SIGNATURE_PATTERN,
  SESSION_MONITORING_COMMAND_ACTIONS,
  isSessionCommandStatus,
  isSessionMonitoringCommandAction,
  isSessionSource,
  sessionCommandSigningPayload,
  type SessionCommandPayload,
  type SessionCommandResult,
  type SessionCommandStatus,
  type SessionMonitoringCommandAction,
  type SessionSource,
} from '@luminis/protocol';
import { signStandardBase64 } from './crypto/e2e';
import {
  newSessionCommandId,
  newSessionCommandNonce,
  sessionCommandErrorCopy,
  sha256Hex,
} from './session-control';
import { nowSeconds } from './id';
import { loadJson, saveJson } from './storage';
import type { SessionView } from './types';

// ---------------------------------------------------------------------------
// Actions & payloads
// ---------------------------------------------------------------------------

/**
 * Additive `session.command` actions for monitoring (not control capabilities).
 * The protocol action union landed in v0.5.0; this alias keeps the phone model
 * readable and prevents accidental reuse of control-only helpers.
 */
export type SessionMonitoringAction = SessionMonitoringCommandAction;

export const SESSION_MONITORING_ACTIONS: readonly SessionMonitoringAction[] =
  SESSION_MONITORING_COMMAND_ACTIONS;

export function isSessionMonitoringAction(value: unknown): value is SessionMonitoringAction {
  return isSessionMonitoringCommandAction(value);
}

/**
 * Signed monitoring command. Deliberately has no `text`/`mode` fields: the
 * daemon rejects any such combination with a fixed `INVALID_REQUEST`.
 */
export interface SessionMonitoringCommandPayload extends SessionCommandPayload {
  source: SessionSource;
  session_id: string;
  command: SessionMonitoringAction;
  command_id: string;
  nonce: string;
  /** Phone-generated Unix seconds; part of the signed canonical transcript. */
  issued_at: number;
  /** Standard-base64 Ed25519 signature over `sessionCommandSigningPayload`. */
  signature: string;
  /** Monitoring commands are metadata-only (never signed/sent with text). */
  text?: never;
  /** Monitoring commands never carry a message admission mode. */
  mode?: never;
}

export interface BuildSignedSessionMonitoringCommandOptions {
  source: SessionSource;
  sessionId: string;
  action: SessionMonitoringAction;
  signingPrivateKey: string;
  commandId?: string;
  nonce?: string;
  /** Injectable clock for tests (defaults to Date.now). */
  now?: () => number;
}

export type BuildSignedSessionMonitoringCommandResult =
  | { ok: true; payload: SessionMonitoringCommandPayload; transcript: string }
  | { ok: false; code: MonitoringBuildErrorCode; message: string };

export type MonitoringBuildErrorCode =
  'INVALID_ACTION' | 'INVALID_TARGET' | 'INVALID_PAYLOAD' | 'SIGNING_FAILED';

function validTarget(source: unknown, sessionId: unknown): boolean {
  return (
    isSessionSource(source) &&
    typeof sessionId === 'string' &&
    sessionId.length > 0 &&
    sessionId.length <= 512
  );
}

/**
 * Narrow signed-payload guard.
 *
 * Checks the exact metadata-only wire shape (monitoring action, required
 * id/nonce/timestamp/signature, no `text`/`mode`) independently of the broader
 * protocol command guard. The build path pins the payload to this type.
 */
export function isSignedSessionMonitoringCommandPayload(
  value: unknown,
): value is SessionMonitoringCommandPayload {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  if (!validTarget(candidate['source'], candidate['session_id'])) return false;
  if (!isSessionMonitoringAction(candidate['command'])) return false;
  // Monitoring commands are metadata-only: text/mode must be absent.
  if (candidate['text'] !== undefined || candidate['mode'] !== undefined) return false;
  if (
    typeof candidate['command_id'] !== 'string' ||
    !SESSION_COMMAND_ID_PATTERN.test(candidate['command_id'])
  ) {
    return false;
  }
  if (
    typeof candidate['nonce'] !== 'string' ||
    !SESSION_COMMAND_NONCE_PATTERN.test(candidate['nonce'])
  ) {
    return false;
  }
  if (
    typeof candidate['issued_at'] !== 'number' ||
    !Number.isSafeInteger(candidate['issued_at']) ||
    candidate['issued_at'] <= 0
  ) {
    return false;
  }
  if (
    typeof candidate['signature'] !== 'string' ||
    candidate['signature'].length % 4 !== 0 ||
    !SESSION_COMMAND_SIGNATURE_PATTERN.test(candidate['signature'])
  ) {
    return false;
  }
  return true;
}

/**
 * Build and sign `ignore`/`unignore` with the phone Ed25519 key.
 *
 * The signed transcript is the protocol canonical one with an empty text hash
 * (there is no text) and no `mode` field. IDs/nonce/`issued_at` are generated by
 * the phone so the daemon can reject replays and stale signatures.
 */
export async function buildSignedSessionMonitoringCommand(
  options: BuildSignedSessionMonitoringCommandOptions,
): Promise<BuildSignedSessionMonitoringCommandResult> {
  if (!isSessionMonitoringAction(options.action)) {
    return { ok: false, code: 'INVALID_ACTION', message: '不支持的监视命令动作' };
  }
  if (!validTarget(options.source, options.sessionId)) {
    return { ok: false, code: 'INVALID_TARGET', message: '目标会话参数无效' };
  }
  const commandId = options.commandId ?? newSessionCommandId();
  const nonce = options.nonce ?? newSessionCommandNonce();
  const nowMs = (options.now ?? Date.now)();
  const issuedAt = Math.floor(nowMs / 1000);
  if (!Number.isSafeInteger(issuedAt) || issuedAt <= 0) {
    return { ok: false, code: 'INVALID_PAYLOAD', message: '命令时间戳无效（issued_at）' };
  }
  const base = {
    source: options.source,
    session_id: options.sessionId,
    command: options.action,
    command_id: commandId,
    nonce,
    issued_at: issuedAt,
  };
  const textHash = await sha256Hex('');
  const transcript = sessionCommandSigningPayload(base, textHash);
  let signature: string;
  try {
    signature = await signStandardBase64(options.signingPrivateKey, transcript);
  } catch {
    return { ok: false, code: 'SIGNING_FAILED', message: '监视命令签名失败' };
  }
  const payload: SessionMonitoringCommandPayload = { ...base, signature };
  if (!isSignedSessionMonitoringCommandPayload(payload)) {
    return { ok: false, code: 'INVALID_PAYLOAD', message: '监视命令 payload 未通过校验' };
  }
  return { ok: true, payload, transcript };
}

// ---------------------------------------------------------------------------
// Local persisted exclusion list
// ---------------------------------------------------------------------------

export const SESSION_MONITORING_STORAGE_KEY = 'session_monitoring';
export const SESSION_MONITORING_STORE_VERSION = 1 as const;
export const MAX_SESSION_EXCLUSIONS = 200;
export const MAX_MONITORING_COMMANDS = 50;
/** Client-side wait for a result before marking the command uncertain. */
export const SESSION_MONITORING_RESULT_TIMEOUT_MS = 30_000;
/** Persisted pending commands older than this are settled as timeouts on load. */
export const SESSION_MONITORING_PENDING_TTL_SECONDS = 120;

export type SessionExclusionMode = 'manual' | 'auto';
export type SessionExclusionReason = 'manual' | 'inactive_7d' | 'corrupt';

export interface SessionExclusionEntry {
  source: SessionSource;
  session_id: string;
  mode: SessionExclusionMode;
  reason: SessionExclusionReason;
  /** Unix seconds (first exclusion). */
  created_at: number;
  /** Unix seconds (last local update). */
  updated_at: number;
  /** Display label captured from the session list (optional, local only). */
  title?: string;
  /** A signed command is in flight; UI disables duplicate actions. */
  pending?: boolean;
  last_command_id?: string;
  /** Redacted, user-facing failure copy (never raw server detail/paths). */
  last_error?: string;
}

export type SessionMonitoringCommandState =
  'sent' | 'accepted' | 'succeeded' | 'failed' | 'timeout';

export interface SessionMonitoringCommandRecord {
  command_id: string;
  action: SessionMonitoringAction;
  source: SessionSource;
  session_id: string;
  /** Exclusion mode/reason before the command (used for copy + rollback). */
  entry_mode: SessionExclusionMode;
  entry_reason: SessionExclusionReason;
  state: SessionMonitoringCommandState;
  created_at: number;
  updated_at: number;
  /** Fixed protocol error code (no detail/paths). */
  code?: string;
  /** Snapshot restored if an optimistic change is rolled back. */
  previous_entry?: SessionExclusionEntry;
}

export interface SessionMonitoringStore {
  version: typeof SESSION_MONITORING_STORE_VERSION;
  updated_at: number;
  entries: SessionExclusionEntry[];
  /** Keys (`source|session_id`) manually resumed so auto rules do not re-add them. */
  manual_overrides: Record<string, number>;
  commands: Record<string, SessionMonitoringCommandRecord>;
}

export type SessionMonitoringNoticeTone = 'pending' | 'success' | 'error';

export interface SessionMonitoringNotice {
  tone: SessionMonitoringNoticeTone;
  message: string;
  at: number;
}

export interface SessionMonitoringSendResult {
  ok: boolean;
  command_id?: string;
  error?: string;
}

export function sessionExclusionKey(
  source: Pick<SessionView, 'source'>['source'],
  sessionId: string,
): string {
  return `${source}|${sessionId}`;
}

export function createEmptySessionMonitoringStore(): SessionMonitoringStore {
  return {
    version: SESSION_MONITORING_STORE_VERSION,
    updated_at: 0,
    entries: [],
    manual_overrides: {},
    commands: {},
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function finiteSeconds(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? Math.floor(value)
    : undefined;
}

function capText(value: unknown, max: number): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (trimmed.length === 0) return undefined;
  return trimmed.length > max ? `${trimmed.slice(0, max)}…` : trimmed;
}

function normalizeEntry(raw: unknown, now: number): SessionExclusionEntry | null {
  if (!isRecord(raw)) return null;
  const source = raw['source'];
  const sessionId = raw['session_id'];
  if (!isSessionSource(source)) return null;
  if (typeof sessionId !== 'string' || sessionId.length === 0 || sessionId.length > 512) {
    return null;
  }
  const mode: SessionExclusionMode = raw['mode'] === 'auto' ? 'auto' : 'manual';
  let reason: SessionExclusionReason;
  if (mode === 'manual') {
    reason = 'manual';
  } else {
    reason = raw['reason'] === 'corrupt' ? 'corrupt' : 'inactive_7d';
  }
  const createdAt = finiteSeconds(raw['created_at']) ?? now;
  const updatedAt = finiteSeconds(raw['updated_at']) ?? createdAt;
  const entry: SessionExclusionEntry = {
    source,
    session_id: sessionId,
    mode,
    reason,
    created_at: createdAt,
    updated_at: updatedAt,
    ...(capText(raw['title'], 200) !== undefined ? { title: capText(raw['title'], 200) } : {}),
    pending: raw['pending'] === true,
    ...(capText(raw['last_command_id'], 128) !== undefined
      ? { last_command_id: capText(raw['last_command_id'], 128) }
      : {}),
    ...(capText(raw['last_error'], 200) !== undefined
      ? { last_error: capText(raw['last_error'], 200) }
      : {}),
  };
  return entry;
}

function normalizeCommandRecord(raw: unknown): SessionMonitoringCommandRecord | null {
  if (!isRecord(raw)) return null;
  const commandId = raw['command_id'];
  if (typeof commandId !== 'string' || commandId.length === 0) return null;
  if (!isSessionMonitoringAction(raw['action'])) return null;
  if (!isSessionSource(raw['source'])) return null;
  const sessionId = raw['session_id'];
  if (typeof sessionId !== 'string' || sessionId.length === 0) return null;
  const state = raw['state'];
  if (
    state !== 'sent' &&
    state !== 'accepted' &&
    state !== 'succeeded' &&
    state !== 'failed' &&
    state !== 'timeout'
  ) {
    return null;
  }
  const createdAt = finiteSeconds(raw['created_at']) ?? 0;
  if (createdAt === 0) return null;
  const previousEntry = normalizeEntry(raw['previous_entry'], createdAt);
  return {
    command_id: commandId,
    action: raw['action'],
    source: raw['source'],
    session_id: sessionId,
    entry_mode: raw['entry_mode'] === 'auto' ? 'auto' : 'manual',
    entry_reason:
      raw['entry_reason'] === 'inactive_7d'
        ? 'inactive_7d'
        : raw['entry_reason'] === 'corrupt'
          ? 'corrupt'
          : 'manual',
    state,
    created_at: createdAt,
    updated_at: finiteSeconds(raw['updated_at']) ?? createdAt,
    ...(capText(raw['code'], 64) !== undefined ? { code: capText(raw['code'], 64) } : {}),
    ...(previousEntry !== null ? { previous_entry: previousEntry } : {}),
  };
}

function trimEntries(entries: SessionExclusionEntry[]): SessionExclusionEntry[] {
  const sorted = [...entries].sort((a, b) => b.created_at - a.created_at);
  return sorted.slice(0, MAX_SESSION_EXCLUSIONS);
}

function trimCommands(
  commands: Record<string, SessionMonitoringCommandRecord>,
): Record<string, SessionMonitoringCommandRecord> {
  const rows = Object.values(commands).sort((a, b) => b.created_at - a.created_at);
  const kept = rows.slice(0, MAX_MONITORING_COMMANDS);
  return Object.fromEntries(kept.map((row) => [row.command_id, row]));
}

function trimOverrides(overrides: Record<string, number>): Record<string, number> {
  const rows = Object.entries(overrides)
    .filter(([, at]) => typeof at === 'number' && Number.isFinite(at))
    .sort((a, b) => b[1] - a[1])
    .slice(0, MAX_SESSION_EXCLUSIONS * 2);
  return Object.fromEntries(rows);
}

/**
 * Normalize anything read from `localStorage` (or a future server listing)
 * into a bounded, deduplicated store. Invalid entries are dropped silently —
 * never rendered or logged.
 */
export function normalizeSessionMonitoringStore(
  raw: unknown,
  now = nowSeconds(),
): SessionMonitoringStore {
  if (!isRecord(raw)) return createEmptySessionMonitoringStore();
  const nowValue = finiteSeconds(now) ?? nowSeconds();

  const byKey = new Map<string, SessionExclusionEntry>();
  const entriesRaw = Array.isArray(raw['entries']) ? raw['entries'] : [];
  for (const item of entriesRaw) {
    const entry = normalizeEntry(item, nowValue);
    if (!entry) continue;
    byKey.set(sessionExclusionKey(entry.source, entry.session_id), entry);
  }
  const entries = trimEntries([...byKey.values()]);

  const overrides: Record<string, number> = {};
  if (isRecord(raw['manual_overrides'])) {
    for (const [key, at] of Object.entries(raw['manual_overrides'])) {
      const seconds = finiteSeconds(at);
      if (seconds !== undefined && key.length > 0 && key.length <= 600) overrides[key] = seconds;
    }
  }

  const commands: Record<string, SessionMonitoringCommandRecord> = {};
  if (isRecord(raw['commands'])) {
    for (const item of Object.values(raw['commands'])) {
      const record = normalizeCommandRecord(item);
      if (record) commands[record.command_id] = record;
    }
  }

  const store: SessionMonitoringStore = {
    version: SESSION_MONITORING_STORE_VERSION,
    updated_at: nowValue,
    entries,
    manual_overrides: trimOverrides(overrides),
    commands: trimCommands(commands),
  };
  // A pending command persisted across an app restart can no longer receive its
  // result; settle it explicitly so the UI never shows an eternal spinner.
  return pruneStaleMonitoringCommands(store, nowValue, SESSION_MONITORING_PENDING_TTL_SECONDS);
}

export function loadSessionMonitoringStore(now = nowSeconds()): SessionMonitoringStore {
  return normalizeSessionMonitoringStore(
    loadJson<unknown>(SESSION_MONITORING_STORAGE_KEY, null),
    now,
  );
}

export function saveSessionMonitoringStore(store: SessionMonitoringStore): void {
  saveJson(SESSION_MONITORING_STORAGE_KEY, {
    ...store,
    version: SESSION_MONITORING_STORE_VERSION,
    updated_at: nowSeconds(),
  });
}

export function findSessionExclusion(
  entries: readonly SessionExclusionEntry[],
  source: SessionSource,
  sessionId: string,
): SessionExclusionEntry | undefined {
  const key = sessionExclusionKey(source, sessionId);
  return entries.find((entry) => sessionExclusionKey(entry.source, entry.session_id) === key);
}

export function monitoringHasExcludedSession(
  entries: readonly SessionExclusionEntry[],
  session: Pick<SessionView, 'source' | 'session_id'>,
): boolean {
  return findSessionExclusion(entries, session.source, session.session_id) !== undefined;
}

/** Local hide rule for the session list (pending entries hide immediately). */
export function filterMonitoredSessions<T extends Pick<SessionView, 'source' | 'session_id'>>(
  sessions: readonly T[],
  entries: readonly SessionExclusionEntry[],
): T[] {
  if (entries.length === 0) return [...sessions];
  return sessions.filter((session) => !monitoringHasExcludedSession(entries, session));
}

export interface PartitionedSessionExclusions {
  manual: SessionExclusionEntry[];
  auto: SessionExclusionEntry[];
}

/** Settings sections: user-stopped vs automatic (7d inactivity / unreadable). */
export function partitionSessionExclusions(
  entries: readonly SessionExclusionEntry[],
): PartitionedSessionExclusions {
  const manual: SessionExclusionEntry[] = [];
  const auto: SessionExclusionEntry[] = [];
  for (const entry of entries) {
    if (entry.mode === 'auto') auto.push(entry);
    else manual.push(entry);
  }
  const newest = (a: SessionExclusionEntry, b: SessionExclusionEntry): number =>
    b.updated_at - a.updated_at;
  return { manual: manual.sort(newest), auto: auto.sort(newest) };
}

/**
 * Settings display: manual entries + corrupt auto entries only.
 *
 * 7-day inactivity auto entries stay in the local store (so stale session
 * rows are still filtered) but are completely hidden from the app, including
 * this settings section, per the 2026-10-19 UX request.
 */
export function displaySessionExclusions(
  entries: readonly SessionExclusionEntry[],
): PartitionedSessionExclusions {
  const { manual, auto } = partitionSessionExclusions(entries);
  return { manual, auto: auto.filter((entry) => entry.reason === 'corrupt') };
}

export function sessionExclusionReasonLabel(entry: SessionExclusionEntry): string {
  if (entry.mode === 'manual') return '手动停止';
  return entry.reason === 'corrupt' ? '无法读取' : '7 天无活动';
}

/** Button copy: corrupt auto entries get “重试读取”, everything else “继续监视”. */
export function sessionExclusionResumeLabel(entry: SessionExclusionEntry): string {
  return entry.mode === 'auto' && entry.reason === 'corrupt' ? '重试读取' : '继续监视';
}

export function sessionExclusionTitle(
  entry: Pick<SessionExclusionEntry, 'source' | 'session_id' | 'title'>,
): string {
  const title = entry.title?.trim();
  return title && title.length > 0 ? title : `${entry.source} / ${entry.session_id}`;
}

// ---------------------------------------------------------------------------
// Store transforms (pure; used by AppState and tested directly)
// ---------------------------------------------------------------------------

export interface BeginMonitoringCommandInput {
  action: SessionMonitoringAction;
  source: SessionSource;
  sessionId: string;
  title?: string;
  commandId: string;
  now?: number;
}

function upsertEntry(
  entries: readonly SessionExclusionEntry[],
  next: SessionExclusionEntry,
): SessionExclusionEntry[] {
  const key = sessionExclusionKey(next.source, next.session_id);
  const filtered = entries.filter(
    (entry) => sessionExclusionKey(entry.source, entry.session_id) !== key,
  );
  return trimEntries([...filtered, next]);
}

function replaceEntry(
  entries: readonly SessionExclusionEntry[],
  next: SessionExclusionEntry,
): SessionExclusionEntry[] {
  return upsertEntry(entries, next);
}

function removeEntry(
  entries: readonly SessionExclusionEntry[],
  source: SessionSource,
  sessionId: string,
): SessionExclusionEntry[] {
  const key = sessionExclusionKey(source, sessionId);
  return entries.filter((entry) => sessionExclusionKey(entry.source, entry.session_id) !== key);
}

/**
 * Optimistically record an in-flight command. `ignore` hides the session
 * immediately (pending manual entry); `unignore` marks the existing entry
 * pending while keeping it visible until the daemon confirms.
 */
export function beginMonitoringCommand(
  store: SessionMonitoringStore,
  input: BeginMonitoringCommandInput,
): SessionMonitoringStore {
  const now = input.now ?? nowSeconds();
  const key = sessionExclusionKey(input.source, input.sessionId);
  const existing = findSessionExclusion(store.entries, input.source, input.sessionId);
  const previous = existing && !existing.pending ? existing : undefined;
  const mode: SessionExclusionMode =
    input.action === 'ignore' ? 'manual' : (existing?.mode ?? 'manual');
  const reason: SessionExclusionReason =
    input.action === 'ignore' ? 'manual' : (existing?.reason ?? 'manual');
  const title = input.title?.trim() ? capText(input.title, 200) : existing?.title;
  const entry: SessionExclusionEntry = {
    source: input.source,
    session_id: input.sessionId,
    mode,
    reason,
    created_at: existing?.created_at ?? now,
    updated_at: now,
    ...(title !== undefined ? { title } : {}),
    pending: true,
    last_command_id: input.commandId,
  };
  const entries = upsertEntry(store.entries, entry);
  const overrides = { ...store.manual_overrides };
  // A user-initiated ignore wins over an earlier manual override.
  if (input.action === 'ignore') delete overrides[key];
  const record: SessionMonitoringCommandRecord = {
    command_id: input.commandId,
    action: input.action,
    source: input.source,
    session_id: input.sessionId,
    entry_mode: mode,
    entry_reason: reason,
    state: 'sent',
    created_at: now,
    updated_at: now,
    ...(previous ? { previous_entry: previous } : {}),
  };
  return {
    ...store,
    updated_at: now,
    entries,
    manual_overrides: trimOverrides(overrides),
    commands: trimCommands({ ...store.commands, [input.commandId]: record }),
  };
}

/** The payload was never handed to the relay: roll the optimistic change back. */
export function failMonitoringSend(
  store: SessionMonitoringStore,
  commandId: string,
  error?: string,
  now = nowSeconds(),
): SessionMonitoringStore {
  const record = store.commands[commandId];
  if (!record || record.state === 'succeeded') return store;
  return settleMonitoringCommand(store, record, 'failed', undefined, error, now);
}

function settleMonitoringCommand(
  store: SessionMonitoringStore,
  record: SessionMonitoringCommandRecord,
  outcome: 'succeeded' | 'failed' | 'timeout' | 'unknown',
  code?: string,
  errorCopy?: string,
  now = nowSeconds(),
): SessionMonitoringStore {
  const key = sessionExclusionKey(record.source, record.session_id);
  const current = findSessionExclusion(store.entries, record.source, record.session_id);
  let entries = store.entries;
  const overrides = { ...store.manual_overrides };

  if (outcome === 'succeeded') {
    if (record.action === 'ignore') {
      entries = upsertEntry(entries, {
        source: record.source,
        session_id: record.session_id,
        mode: 'manual',
        reason: 'manual',
        created_at: current?.created_at ?? now,
        updated_at: now,
        ...(current?.title !== undefined ? { title: current.title } : {}),
        pending: false,
        last_command_id: record.command_id,
      });
      delete overrides[key];
    } else {
      const previous = current ?? record.previous_entry;
      entries = removeEntry(entries, record.source, record.session_id);
      // Manual “continue monitoring” of an auto entry is sticky: auto rules must
      // not re-add it locally. (The daemon records the same manual override.)
      if (previous?.mode === 'auto') overrides[key] = now;
    }
  } else if (outcome === 'failed') {
    if (record.action === 'ignore') {
      // Definite failure: the daemon did not stop monitoring, restore the
      // previous state (usually no entry at all).
      entries = record.previous_entry
        ? replaceEntry(entries, { ...record.previous_entry, pending: false })
        : removeEntry(entries, record.source, record.session_id);
    } else if (current) {
      entries = replaceEntry(entries, {
        ...current,
        pending: false,
        updated_at: now,
        ...(errorCopy !== undefined ? { last_error: capText(errorCopy, 200) } : {}),
      });
    }
  } else {
    // timeout / unknown: the daemon may or may not have applied it. Keep the
    // local state (so “继续监视” remains reachable) but stop the pending spin.
    if (current) {
      entries = replaceEntry(entries, {
        ...current,
        pending: false,
        updated_at: now,
        ...(errorCopy !== undefined ? { last_error: capText(errorCopy, 200) } : {}),
      });
    }
  }

  const nextRecord: SessionMonitoringCommandRecord = {
    ...record,
    state: outcome === 'succeeded' ? 'succeeded' : outcome === 'failed' ? 'failed' : 'timeout',
    updated_at: now,
    ...(code !== undefined ? { code } : {}),
  };
  return {
    ...store,
    updated_at: now,
    entries: trimEntries(entries),
    manual_overrides: trimOverrides(overrides),
    commands: trimCommands({ ...store.commands, [record.command_id]: nextRecord }),
  };
}

/**
 * Apply a daemon result for a monitoring command. Replays are idempotent; a
 * late `succeeded` may upgrade an earlier timeout/unknown/failed outcome (the
 * daemon is authoritative about the actual exclusion), while a late failure
 * never downgrades a success.
 */
export function applyMonitoringCommandResult(
  store: SessionMonitoringStore,
  result: SessionCommandResult,
  now = nowSeconds(),
): SessionMonitoringStore {
  const record = store.commands[result.command_id];
  if (!record) return store;
  if (!isSessionCommandStatus(result.status)) return store;
  if (record.state === 'succeeded') return store;
  if (result.status === 'accepted') {
    if (record.state !== 'sent') return store;
    return {
      ...store,
      commands: {
        ...store.commands,
        [record.command_id]: { ...record, state: 'accepted', updated_at: now },
      },
    };
  }
  if (result.status === 'succeeded') {
    return settleMonitoringCommand(store, record, 'succeeded', result.code, undefined, now);
  }
  const outcome: 'failed' | 'timeout' | 'unknown' =
    result.status === 'failed' || result.status === 'rejected'
      ? 'failed'
      : result.status === 'timeout'
        ? 'timeout'
        : 'unknown';
  // A definite failure shows the redacted daemon reason; timeout/unknown keeps
  // the local hidden state and explains how to restore it (no auto retry).
  const copy =
    outcome === 'failed'
      ? sessionCommandErrorCopy({ code: result.code, detail: result.detail })
      : SESSION_MONITORING_TIMEOUT_COPY;
  return settleMonitoringCommand(store, record, outcome, result.code, copy, now);
}

/** Client-side result timeout (no automatic retry). */
export function applyMonitoringTimeout(
  store: SessionMonitoringStore,
  commandId: string,
  now = nowSeconds(),
): SessionMonitoringStore {
  const record = store.commands[commandId];
  if (!record || record.state === 'succeeded' || record.state === 'failed') return store;
  return settleMonitoringCommand(
    store,
    record,
    'timeout',
    'TIMEOUT',
    SESSION_MONITORING_TIMEOUT_COPY,
    now,
  );
}

/**
 * Settle persisted `sent`/`accepted` commands after a restart (they can no
 * longer receive a result). Idempotent.
 */
export function pruneStaleMonitoringCommands(
  store: SessionMonitoringStore,
  now = nowSeconds(),
  maxAgeSeconds = SESSION_MONITORING_PENDING_TTL_SECONDS,
): SessionMonitoringStore {
  let next = store;
  for (const record of Object.values(store.commands)) {
    if (record.state !== 'sent' && record.state !== 'accepted') continue;
    if (now - record.created_at <= maxAgeSeconds) continue;
    next = applyMonitoringTimeout(next, record.command_id, now);
  }
  return next;
}

/**
 * Preferred path once the relay exposes a server-side exclusion listing: the
 * server list is authoritative, so local entries are replaced (pending local
 * commands keep their optimistic entry until their result lands). Not wired
 * yet because no protocol listing message exists in this tree.
 */
export interface ServerSessionExclusionWire {
  source: SessionSource;
  session_id: string;
  mode: 'manual' | 'auto';
  reason: 'user' | 'inactive_7d' | 'corrupt';
  updated_at: number;
}

/** Validate a server `monitoring_exclusions` array; malformed entries are dropped. */
export function parseServerExclusionList(value: unknown): SessionExclusionEntry[] {
  if (!Array.isArray(value)) return [];
  const result: SessionExclusionEntry[] = [];
  const seen = new Set<string>();
  for (const raw of value) {
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) continue;
    const entry = raw as Record<string, unknown>;
    const source = entry['source'];
    const sessionId = entry['session_id'];
    const mode = entry['mode'];
    const reason = entry['reason'];
    const updatedAt = entry['updated_at'];
    if (!isSessionSource(source) || typeof sessionId !== 'string' || sessionId.length === 0) {
      continue;
    }
    if (mode !== 'manual' && mode !== 'auto') continue;
    if (reason !== 'user' && reason !== 'inactive_7d' && reason !== 'corrupt') continue;
    if (typeof updatedAt !== 'number' || !Number.isSafeInteger(updatedAt) || updatedAt < 0) {
      continue;
    }
    const key = sessionExclusionKey(source, sessionId);
    if (seen.has(key)) continue;
    seen.add(key);
    result.push({
      source,
      session_id: sessionId,
      mode,
      reason: reason === 'user' ? 'manual' : reason,
      created_at: updatedAt,
      updated_at: updatedAt,
    });
  }
  return result;
}

export function applyServerExclusionList(
  store: SessionMonitoringStore,
  serverEntries: readonly SessionExclusionEntry[],
  now = nowSeconds(),
): SessionMonitoringStore {
  const pendingKeys = new Set(
    Object.values(store.commands)
      .filter((record) => record.state === 'sent' || record.state === 'accepted')
      .map((record) => sessionExclusionKey(record.source, record.session_id)),
  );
  const serverKeys = new Set(
    serverEntries.map((entry) => sessionExclusionKey(entry.source, entry.session_id)),
  );
  const pending = store.entries.filter((entry) =>
    pendingKeys.has(sessionExclusionKey(entry.source, entry.session_id)),
  );
  const server = serverEntries.filter(
    (entry) => !pendingKeys.has(sessionExclusionKey(entry.source, entry.session_id)),
  );
  return {
    ...store,
    updated_at: now,
    entries: trimEntries([...server, ...pending]),
    manual_overrides: Object.fromEntries(
      Object.entries(store.manual_overrides).filter(([key]) => !serverKeys.has(key)),
    ),
  };
}

// ---------------------------------------------------------------------------
// Fixed UX copy (no raw server text, no paths/secrets)
// ---------------------------------------------------------------------------

export const SESSION_MONITORING_MENU_LABEL = '不再监视此会话';
export const SESSION_MONITORING_CONFIRM_TITLE = '不再监视此会话？';
export const SESSION_MONITORING_CONFIRM_LINES: readonly string[] = [
  'daemon 将停止扫描/读取此会话（只读监视），该会话会从默认列表隐藏。',
  '不会删除或修改会话内容，也不会写入 .dsh；其他会话不受影响。',
  '可在「设置 → 已停止监视的会话」恢复；恢复后从当前进度继续，不回填历史。',
];
export const SESSION_MONITORING_CONFIRM_ACTION = '停止监视';
export const SESSION_MONITORING_PENDING_COPY = '正在等待 daemon 确认…';
export const SESSION_MONITORING_SUCCESS_COPY =
  '已停止监视此会话；可在「设置 → 已停止监视的会话」恢复。';
export const SESSION_MONITORING_RESUME_SUCCESS_COPY =
  '已恢复监视此会话；重新拉取列表后会出现（不回填历史）。';
export const SESSION_MONITORING_RETRY_SUCCESS_COPY =
  '已请求重新读取；若仍无法读取，daemon 会再次自动停止监视（不会反复重试）。';
export const SESSION_MONITORING_SEND_FAILED_COPY = '停止监视请求未发送，本地状态已回滚。';
export const SESSION_MONITORING_TIMEOUT_COPY =
  '未收到 daemon 确认；已保留本地状态，可在设置中「继续监视」（不会自动重试）。';
export const SESSION_MONITORING_IGNORE_FAILED_COPY = '停止监视失败';
export const SESSION_MONITORING_RESUME_FAILED_COPY = '恢复监视失败，会话保持隐藏';
export const SESSION_MONITORING_AUTO_INACTIVE_COPY =
  '7 天以上无活动的会话由 daemon 自动停止监视；重新有活动或手动「继续监视」可恢复。';
export const SESSION_MONITORING_AUTO_CORRUPT_COPY =
  '无法读取/解析的会话由 daemon 自动停止监视；文件变化或手动「重试读取」可恢复。';
export const SESSION_MONITORING_LARGE_SESSION_COPY =
  '大会话仍继续监视（不会按体积自动停止）；需要时可手动「不再监视此会话」。';
export const SESSION_MONITORING_SECTION_HINT =
  '只停止 daemon 的只读扫描/监视，不删除会话内容，不写 .dsh。';
export const SESSION_MONITORING_EMPTY_MANUAL_COPY =
  '没有手动停止监视的会话。可在会话详情「⋯ → 不再监视此会话」中停止。';
export const SESSION_MONITORING_EMPTY_AUTO_COPY =
  '没有自动停止监视的会话。daemon 会在会话 7 天无活动或无法读取时自动停止监视。';

export function sessionMonitoringResultNotice(
  record: SessionMonitoringCommandRecord,
  result: SessionCommandResult,
  now = nowSeconds(),
): SessionMonitoringNotice | null {
  if (result.status === 'accepted') return null;
  if (result.status === 'succeeded') {
    if (record.action === 'ignore') {
      return { tone: 'success', message: SESSION_MONITORING_SUCCESS_COPY, at: now };
    }
    return {
      tone: 'success',
      message:
        record.entry_reason === 'corrupt'
          ? SESSION_MONITORING_RETRY_SUCCESS_COPY
          : SESSION_MONITORING_RESUME_SUCCESS_COPY,
      at: now,
    };
  }
  if (result.status === 'failed' || result.status === 'rejected') {
    const reason = sessionCommandErrorCopy({ code: result.code, detail: result.detail });
    return {
      tone: 'error',
      message:
        record.action === 'ignore'
          ? `${SESSION_MONITORING_IGNORE_FAILED_COPY}：${reason}`
          : `${SESSION_MONITORING_RESUME_FAILED_COPY}：${reason}`,
      at: now,
    };
  }
  return {
    tone: 'error',
    message:
      record.action === 'ignore'
        ? SESSION_MONITORING_TIMEOUT_COPY
        : `${SESSION_MONITORING_RESUME_FAILED_COPY}（结果未知）`,
    at: now,
  };
}

export function sessionMonitoringTimeoutNotice(
  record: SessionMonitoringCommandRecord,
  now = nowSeconds(),
): SessionMonitoringNotice {
  return {
    tone: 'error',
    message:
      record.action === 'ignore'
        ? SESSION_MONITORING_TIMEOUT_COPY
        : `${SESSION_MONITORING_RESUME_FAILED_COPY}（等待超时）`,
    at: now,
  };
}
